import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Mail, Trash2, Save, Eye, EyeOff, Lock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import toast from 'react-hot-toast';
import { supabase} from '../lib/supabase';

const profileSchema = z.object({
  email: z.string().email('Email invalide'),
  fullName: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Le mot de passe actuel est requis'),
  newPassword: z.string().min(8, 'Le nouveau mot de passe doit contenir au moins 8 caractères'),
  confirmPassword: z.string().min(8, 'La confirmation est requise'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

export default function ProfilePage() {
  const { user, userProfile, updateProfile, deactivateAccount, eraseAccount } = useAuth();
  const navigate = useNavigate();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showEraseConfirm, setShowEraseConfirm] = useState(false);
  const [eraseConfirmText, setEraseConfirmText] = useState('');
  const [eraseLoading, setEraseLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      email: userProfile?.email || '',
      fullName: userProfile?.full_name || '',
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  const handleUpdateProfile = async (data: ProfileForm) => {
    setProfileLoading(true);
    try {
      // Mettre à jour l'email dans Supabase Auth si nécessaire
      if (data.email !== user?.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: data.email,
        });
        if (emailError) {
          toast.error('Erreur lors de la mise à jour de l\'email: ' + emailError.message);
          setProfileLoading(false);
          return;
        }
        toast('Un email de confirmation a été envoyé à votre nouvelle adresse', {
          icon: 'ℹ️',
        });
      }

      // Mettre à jour le profil dans notre table. L'email de public.users est
      // synchronisé par trigger depuis auth.users une fois le changement confirmé.
      const { error } = await updateProfile({
        full_name: data.fullName,
      });

      if (error) {
        toast.error('Erreur lors de la mise à jour du profil');
      } else {
        toast.success('Profil mis à jour avec succès !');
      }
    } catch (error) {
      toast.error('Une erreur est survenue');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUpdatePassword = async (formData: PasswordForm) => {
    setPasswordLoading(true);
    try {
      // Vérifier d'abord le mot de passe actuel en tentant une reconnexion
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: formData.currentPassword,
      });
      
      if (signInError) {
        toast.error('Mot de passe actuel incorrect');
        setPasswordLoading(false);
        return;
      }

      // Si le mot de passe actuel est correct, procéder à la mise à jour
      const { error } = await supabase.auth.updateUser({ password: formData.newPassword });
      if (error) {
        toast.error('Erreur lors de la mise à jour du mot de passe: ' + error.message);
      } else {
        toast.success('Mot de passe mis à jour avec succès !');
        passwordForm.reset();
      }
    } catch (err) {
      toast.error('Une erreur est survenue');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    setDeleteLoading(true);
    try {
      const { error } = await deactivateAccount();
      if (error) {
        toast.error('Erreur lors de la désactivation du compte');
      } else {
        toast.success('Compte désactivé');
      }
    } catch (error) {
      toast.error('Une erreur est survenue');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleEraseAccount = async () => {
    if (eraseConfirmText !== 'SUPPRIMER') return;

    setEraseLoading(true);
    try {
      const { error } = await eraseAccount();
      if (error) {
        toast.error('Erreur lors de la suppression du compte. Vos données n\'ont pas été supprimées entièrement, réessayez ou contactez le support.');
        setEraseLoading(false);
        return;
      }
      toast.success('Votre compte et vos données personnelles ont été supprimés');
      navigate('/');
    } catch (error) {
      toast.error('Une erreur est survenue');
      setEraseLoading(false);
    }
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Mon Profil
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Gérez vos informations personnelles et paramètres de sécurité
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Informations personnelles */}
            <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
              <div className="flex items-center mb-6">
                <User className="h-6 w-6 text-purple-600 mr-3" />
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Informations personnelles
                </h2>
              </div>

              <form onSubmit={profileForm.handleSubmit(handleUpdateProfile)} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nom complet
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      {...profileForm.register('fullName')}
                      type="text"
                      className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                    />
                  </div>
                  {profileForm.formState.errors.fullName && (
                    <p className="mt-1 text-sm text-red-600">{profileForm.formState.errors.fullName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      {...profileForm.register('email')}
                      type="email"
                      className="w-full pl-10 pr-4 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                    />
                  </div>
                  {profileForm.formState.errors.email && (
                    <p className="mt-1 text-sm text-red-600">{profileForm.formState.errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={profileLoading}
                  className="w-full flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-5 w-5 mr-2" />
                  {profileLoading ? 'Mise à jour...' : 'Mettre à jour'}
                </button>
              </form>
            </div>

            {/* Changement de mot de passe */}
            <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6">
              <div className="flex items-center mb-6">
                <Lock className="h-6 w-6 text-purple-600 mr-3" />
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Changer le mot de passe
                </h2>
              </div>

              <form onSubmit={passwordForm.handleSubmit(handleUpdatePassword)} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mot de passe actuel
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      {...passwordForm.register('currentPassword')}
                      type={showPasswords.current ? 'text' : 'password'}
                      className="w-full pl-10 pr-12 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                      placeholder="Entrez votre mot de passe actuel"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.current ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {passwordForm.formState.errors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordForm.formState.errors.currentPassword.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      {...passwordForm.register('newPassword')}
                      type={showPasswords.new ? 'text' : 'password'}
                      className="w-full pl-10 pr-12 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {passwordForm.formState.errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordForm.formState.errors.newPassword.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirmer le nouveau mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      {...passwordForm.register('confirmPassword')}
                      type={showPasswords.confirm ? 'text' : 'password'}
                      className="w-full pl-10 pr-12 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {passwordForm.formState.errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordForm.formState.errors.confirmPassword.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="w-full flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Lock className="h-5 w-5 mr-2" />
                  {passwordLoading ? 'Mise à jour...' : 'Changer le mot de passe'}
                </button>
              </form>
            </div>
          </div>

          {/* Zone dangereuse */}
          <div className="mt-12 bg-red-50/10 dark:bg-red-900/10 backdrop-blur-lg rounded-2xl border border-red-200/20 p-6">
            <div className="flex items-center mb-6">
              <Trash2 className="h-6 w-6 text-pink-500 dark:text-pink-400 mr-3" />
              <h2 className="text-2xl font-semibold text-white">
                Désactivation et suppression du compte
              </h2>
            </div>

            <div className="bg-white/10 dark:bg-gray-900/10 rounded-xl p-4 mb-4">
              <p className="text-white mb-4">
                La désactivation de votre compte est <span className="font-semibold">réversible</span>. Vous pourrez le réactiver à tout moment en vous reconnectant.
              </p>

              {!showDeleteConfirm ? (
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-2 px-6 rounded-lg transition-colors duration-200 shadow-md"
                  >
                    Désactiver mon compte
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-white font-medium">
                    Êtes-vous sûr de vouloir désactiver votre compte ? Vous pourrez le réactiver à tout moment en vous reconnectant.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleteLoading}
                      className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-2 px-6 rounded-lg transition-colors duration-200 shadow-md disabled:opacity-50"
                    >
                      {deleteLoading ? 'Désactivation...' : 'Oui, désactiver mon compte'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-6 rounded-lg transition-colors duration-200"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/10 dark:bg-gray-900/10 rounded-xl p-4">
              <h3 className="text-xl font-semibold text-white mb-2">
                Suppression définitive du compte
              </h3>
              <p className="text-white mb-4">
                Cette action est <span className="font-semibold">irréversible</span>. Votre compte, vos licences
                (y compris les licences payantes), vos activations d'appareils et votre historique de connexion
                sont supprimés. Seules les données de facturation sont conservées sans lien avec vous, comme
                l'impose la réglementation comptable.
              </p>

              {!showEraseConfirm ? (
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowEraseConfirm(true)}
                    className="bg-red-600 hover:bg-red-700 text-white py-2 px-6 rounded-lg transition-colors duration-200 shadow-md"
                  >
                    Supprimer définitivement mon compte
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-white font-medium">
                    Vous perdrez l'accès à vos licences, sans remboursement. Pour confirmer, tapez{' '}
                    <span className="font-mono font-bold">SUPPRIMER</span> ci-dessous.
                  </p>
                  <input
                    type="text"
                    value={eraseConfirmText}
                    onChange={(e) => setEraseConfirmText(e.target.value)}
                    autoComplete="off"
                    aria-label="Tapez SUPPRIMER pour confirmer"
                    className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
                    placeholder="SUPPRIMER"
                  />
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={handleEraseAccount}
                      disabled={eraseLoading || eraseConfirmText !== 'SUPPRIMER'}
                      className="bg-red-600 hover:bg-red-700 text-white py-2 px-6 rounded-lg transition-colors duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {eraseLoading ? 'Suppression...' : 'Supprimer définitivement'}
                    </button>
                    <button
                      onClick={() => {
                        setShowEraseConfirm(false);
                        setEraseConfirmText('');
                      }}
                      disabled={eraseLoading}
                      className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-6 rounded-lg transition-colors duration-200"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}
